import React, { useState } from 'react';
import { makeStyles, tokens, Dropdown, Option } from '@fluentui/react-components';
import { dataVisualizationTokens } from '../../../tokens';

type ChartType = 'bar' | 'line' | 'pie' | 'donut' | 'area' | 'scatter' | 'heatmap' | 'gauge';

const useStyles = makeStyles({
  container: {
    padding: '40px',
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: '100vh',
    fontFamily: '"Segoe UI", sans-serif',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  section: {
    marginBottom: '48px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '16px',
  },
  sectionDescription: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '20px',
  },
  subsectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginTop: '20px',
    marginBottom: '12px',
    color: tokens.colorNeutralForeground1,
  },
  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  paletteGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },
  colorCard: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  colorSwatch: {
    height: '80px',
    width: '100%',
  },
  colorInfo: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  colorName: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  colorValue: {
    fontSize: '11px',
    fontFamily: 'Consolas, Monaco, monospace',
    color: tokens.colorNeutralForeground2,
  },
  sequentialGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '24px',
  },
  sequentialFamily: {
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  sequentialFamilyName: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  sequentialColors: {
    display: 'flex',
    flexDirection: 'column',
  },
  sequentialColor: {
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
  },
  sequentialColorName: {
    fontSize: '11px',
    fontWeight: 500,
  },
  sequentialColorValue: {
    fontSize: '10px',
    fontFamily: 'Consolas, Monaco, monospace',
  },
  divergingBar: {
    display: 'flex',
    height: '60px',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '12px',
  },
  divergingSegment: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  divergingLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'white',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  chartExample: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginTop: '20px',
  },
  barChart: {
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  barChartTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '20px',
    color: tokens.colorNeutralForeground1,
  },
  bars: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  barLabel: {
    width: '80px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  barFill: {
    height: '32px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '12px',
    fontSize: '12px',
    fontWeight: 600,
    color: 'white',
    transition: 'all 0.3s ease',
    ':hover': {
      transform: 'scaleX(1.02)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
  },
  pieChart: {
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  pieChartTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '20px',
    color: tokens.colorNeutralForeground1,
  },
  pieCircle: {
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    margin: '0 auto 20px',
    position: 'relative',
  },
  pieLegend: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
  },
  legendColor: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
  },
  // Interactive demo section styles
  demoSection: {
    marginBottom: '48px',
    padding: '32px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  demoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  demoTitle: {
    fontSize: '24px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  demoControls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  demoCanvas: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    padding: '32px',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lineChart: {
    width: '100%',
    height: '300px',
    position: 'relative',
  },
  linePath: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  scatterPlot: {
    width: '100%',
    height: '300px',
    position: 'relative',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '4px',
  },
  scatterPoint: {
    position: 'absolute',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    transition: 'transform 0.2s',
    ':hover': {
      transform: 'scale(1.5)',
    },
  },
  heatmapGrid: {
    display: 'grid',
    gap: '4px',
    gridTemplateColumns: 'repeat(10, 1fr)',
  },
  heatmapCell: {
    aspectRatio: '1',
    borderRadius: '4px',
    transition: 'transform 0.2s',
    ':hover': {
      transform: 'scale(1.1)',
      zIndex: 1,
    },
  },
  gaugeContainer: {
    width: '300px',
    height: '200px',
    position: 'relative',
  },
  gaugeSvg: {
    width: '100%',
    height: '100%',
  },
});

export const ViewResponsesTest: React.FC = () => {
  const styles = useStyles();
  const [selectedChartType, setSelectedChartType] = useState<ChartType>('bar');

  // Chart type options
  const chartTypes: { value: ChartType; label: string }[] = [
    { value: 'bar', label: '📊 Bar Chart' },
    { value: 'line', label: '📈 Line Chart' },
    { value: 'pie', label: '🥧 Pie Chart' },
    { value: 'donut', label: '🍩 Donut Chart' },
    { value: 'area', label: '📉 Area Chart' },
    { value: 'scatter', label: '⚫ Scatter Plot' },
    { value: 'heatmap', label: '🔥 Heatmap' },
    { value: 'gauge', label: '⏱️ Gauge' },
  ];

  // 示例数据
  const barChartData = [
    { label: 'Product A', value: 85, color: dataVisualizationTokens.qualitative.slot1 },
    { label: 'Product B', value: 72, color: dataVisualizationTokens.qualitative.slot2 },
    { label: 'Product C', value: 63, color: dataVisualizationTokens.qualitative.slot3 },
    { label: 'Product D', value: 91, color: dataVisualizationTokens.qualitative.slot4 },
    { label: 'Product E', value: 58, color: dataVisualizationTokens.qualitative.slot5 },
  ];

  const pieChartData = [
    { label: 'Excellent', value: 35, color: dataVisualizationTokens.qualitative.slot1 },
    { label: 'Good', value: 28, color: dataVisualizationTokens.qualitative.slot2 },
    { label: 'Average', value: 20, color: dataVisualizationTokens.qualitative.slot3 },
    { label: 'Poor', value: 12, color: dataVisualizationTokens.qualitative.slot4 },
    { label: 'Very Poor', value: 5, color: dataVisualizationTokens.qualitative.slot5 },
  ];

  // 计算饼图的 conic-gradient
  const getPieGradient = () => {
    const total = pieChartData.reduce((sum, item) => sum + item.value, 0);
    let currentPercent = 0;
    
    return pieChartData.map(item => {
      const percent = (item.value / total) * 100;
      const start = currentPercent;
      currentPercent += percent;
      return `${item.color} ${start}% ${currentPercent}%`;
    }).join(', ');
  };

  // Render different chart types
  const renderInteractiveDemo = () => {
    switch (selectedChartType) {
      case 'bar':
        return (
          <div className={styles.barChart}>
            <h3 className={styles.barChartTitle}>Sales by Product</h3>
            <div className={styles.bars}>
              {barChartData.map((item, index) => (
                <div key={index} className={styles.bar}>
                  <div className={styles.barLabel}>{item.label}</div>
                  <div
                    className={styles.barFill}
                    style={{ 
                      width: `${item.value}%`, 
                      backgroundColor: item.color 
                    }}
                  >
                    {item.value}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'line': {
        const lineData = [45, 52, 48, 65, 72, 68, 85, 91];
        const maxValue = Math.max(...lineData);
        return (
          <div style={{ width: '100%', maxWidth: '600px' }}>
            <h3 className={styles.barChartTitle}>Monthly Growth Trend</h3>
            <svg width="100%" height="250" style={{ overflow: 'visible' }}>
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  {dataVisualizationTokens.sequential.cornflower.tint40 && (
                    <stop offset="0%" stopColor={dataVisualizationTokens.sequential.cornflower.tint40} />
                  )}
                  {dataVisualizationTokens.sequential.cornflower.primary && (
                    <stop offset="100%" stopColor={dataVisualizationTokens.sequential.cornflower.primary} />
                  )}
                </linearGradient>
              </defs>
              {lineData.map((value, i) => {
                const x = (i / (lineData.length - 1)) * 100;
                const y = 100 - ((value / maxValue) * 80);
                const nextX = ((i + 1) / (lineData.length - 1)) * 100;
                const nextY = lineData[i + 1] ? 100 - ((lineData[i + 1] / maxValue) * 80) : y;
                
                return (
                  <g key={i}>
                    {i < lineData.length - 1 && (
                      <line
                        x1={`${x}%`}
                        y1={`${y}%`}
                        x2={`${nextX}%`}
                        y2={`${nextY}%`}
                        stroke="url(#lineGradient)"
                        strokeWidth="3"
                      />
                    )}
                    <circle
                      cx={`${x}%`}
                      cy={`${y}%`}
                      r="6"
                      fill={dataVisualizationTokens.sequential.cornflower.primary}
                    />
                  </g>
                );
              })}
            </svg>
          </div>
        );
      }

      case 'pie':
      case 'donut': {
        const isDonut = selectedChartType === 'donut';
        return (
          <div className={styles.pieChart}>
            <h3 className={styles.pieChartTitle}>Customer Satisfaction</h3>
            <div
              className={styles.pieCircle}
              style={{
                background: `conic-gradient(${getPieGradient()})`,
                ...(isDonut && {
                  maskImage: 'radial-gradient(circle, transparent 40%, black 40%)',
                  WebkitMaskImage: 'radial-gradient(circle, transparent 40%, black 40%)',
                })
              }}
            />
            <div className={styles.pieLegend}>
              {pieChartData.map((item, index) => (
                <div key={index} className={styles.legendItem}>
                  <div className={styles.legendColor} style={{ backgroundColor: item.color }} />
                  <span>{item.label}: {item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'area': {
        const areaData = [30, 40, 35, 50, 65, 55, 70, 85];
        const areaMax = Math.max(...areaData);
        return (
          <div style={{ width: '100%', maxWidth: '600px' }}>
            <h3 className={styles.barChartTitle}>Revenue Over Time</h3>
            <svg width="100%" height="250">
              <defs>
                <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={dataVisualizationTokens.sequential.teal.primary} stopOpacity="0.6" />
                  <stop offset="100%" stopColor={dataVisualizationTokens.sequential.teal.tint40} stopOpacity="0.1" />
                </linearGradient>
              </defs>
              <polygon
                points={areaData.map((value, i) => {
                  const x = (i / (areaData.length - 1)) * 100;
                  const y = 100 - ((value / areaMax) * 80);
                  return `${x}%,${y}%`;
                }).join(' ') + ' 100%,100% 0%,100%'}
                fill="url(#areaGradient)"
                stroke={dataVisualizationTokens.sequential.teal.primary}
                strokeWidth="2"
              />
            </svg>
          </div>
        );
      }

      case 'scatter': {
        const scatterData = Array.from({ length: 30 }, () => ({
          x: Math.random() * 90 + 5,
          y: Math.random() * 90 + 5,
          color: dataVisualizationTokens.qualitative[`slot${Math.floor(Math.random() * 5) + 1}` as keyof typeof dataVisualizationTokens.qualitative]
        }));
        return (
          <div style={{ width: '100%', maxWidth: '500px' }}>
            <h3 className={styles.barChartTitle}>Data Distribution</h3>
            <div className={styles.scatterPlot}>
              {scatterData.map((point, i) => (
                <div
                  key={i}
                  className={styles.scatterPoint}
                  style={{
                    left: `${point.x}%`,
                    top: `${point.y}%`,
                    backgroundColor: point.color,
                  }}
                />
              ))}
            </div>
          </div>
        );
      }

      case 'heatmap': {
        const heatmapData = Array.from({ length: 50 }, () => Math.random());
        const getHeatmapColor = (value: number) => {
          const colors = [
            dataVisualizationTokens.sequential.cornflower.tint40!,
            dataVisualizationTokens.sequential.cornflower.tint20!,
            dataVisualizationTokens.sequential.cornflower.primary!,
            dataVisualizationTokens.sequential.cornflower.shade20!,
            dataVisualizationTokens.sequential.cornflower.shade30!,
          ];
          const index = Math.floor(value * (colors.length - 1));
          return colors[index];
        };
        return (
          <div style={{ width: '100%', maxWidth: '500px' }}>
            <h3 className={styles.barChartTitle}>Activity Heatmap</h3>
            <div className={styles.heatmapGrid}>
              {heatmapData.map((value, i) => (
                <div
                  key={i}
                  className={styles.heatmapCell}
                  style={{ backgroundColor: getHeatmapColor(value) }}
                  title={`Value: ${(value * 100).toFixed(0)}%`}
                />
              ))}
            </div>
          </div>
        );
      }

      case 'gauge': {
        const gaugeValue = 72;
        const getGaugeColor = (value: number) => {
          if (value >= 80) return dataVisualizationTokens.semantic.positive;
          if (value >= 50) return dataVisualizationTokens.threat.warning;
          return dataVisualizationTokens.threat.critical;
        };
        return (
          <div>
            <h3 className={styles.barChartTitle}>Performance Score</h3>
            <div className={styles.gaugeContainer}>
              <svg className={styles.gaugeSvg} viewBox="0 0 200 120">
                {/* Background arc */}
                <path
                  d="M 20 100 A 80 80 0 0 1 180 100"
                  fill="none"
                  stroke={tokens.colorNeutralStroke2}
                  strokeWidth="20"
                />
                {/* Value arc */}
                <path
                  d="M 20 100 A 80 80 0 0 1 180 100"
                  fill="none"
                  stroke={getGaugeColor(gaugeValue)}
                  strokeWidth="20"
                  strokeDasharray={`${(gaugeValue / 100) * 251} 251`}
                />
                {/* Center text */}
                <text x="100" y="85" textAnchor="middle" fontSize="32" fontWeight="bold" fill={tokens.colorNeutralForeground1}>
                  {gaugeValue}%
                </text>
                <text x="100" y="105" textAnchor="middle" fontSize="12" fill={tokens.colorNeutralForeground2}>
                  Performance
                </text>
              </svg>
            </div>
          </div>
        );
      }

      default:
        return <div>Select a chart type</div>;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Data Visualization Color Test</h1>
        <p className={styles.subtitle}>Testing Microsoft Fluent Design System data visualization tokens</p>
      </div>

      {/* Interactive Demo Section */}
      <div className={styles.demoSection}>
        <div className={styles.demoHeader}>
          <h2 className={styles.demoTitle}>📊 Interactive Chart Preview</h2>
          <div className={styles.demoControls}>
            <Dropdown
              value={chartTypes.find(t => t.value === selectedChartType)?.label}
              onOptionSelect={(_, data) => setSelectedChartType(data.optionValue as ChartType)}
              placeholder="Select chart type"
            >
              {chartTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Dropdown>
          </div>
        </div>
        <div className={styles.demoCanvas}>
          {renderInteractiveDemo()}
        </div>
      </div>

      {/* Qualitative Palette */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>1. Qualitative Palette (10 Colors)</h2>
        <p className={styles.sectionDescription}>
          For categorical data where no natural order exists between values
        </p>
        <div className={styles.colorGrid}>
          {Object.entries(dataVisualizationTokens.qualitative).map(([key, color]) => (
            <div key={key} className={styles.colorCard}>
              <div className={styles.colorSwatch} style={{ backgroundColor: color }} />
              <div className={styles.colorInfo}>
                <div className={styles.colorName}>{key}</div>
                <div className={styles.colorValue}>{color}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Qualitative Extended */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>2. Qualitative Extended (5 Colors)</h2>
        <p className={styles.sectionDescription}>
          Additional colors for complex datasets requiring more than 10 categories
        </p>
        <div className={styles.colorGrid}>
          {Object.entries(dataVisualizationTokens.qualitativeExtended).map(([key, color]) => (
            <div key={key} className={styles.colorCard}>
              <div className={styles.colorSwatch} style={{ backgroundColor: color }} />
              <div className={styles.colorInfo}>
                <div className={styles.colorName}>{key}</div>
                <div className={styles.colorValue}>{color}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sequential Palettes */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>3. Sequential Palettes (5 Color Families)</h2>
        <p className={styles.sectionDescription}>
          For ordered data that progresses from low to high values
        </p>
        <div className={styles.sequentialGrid}>
          {Object.entries(dataVisualizationTokens.sequential).map(([familyName, colors]) => (
            <div key={familyName} className={styles.sequentialFamily}>
              <div className={styles.sequentialFamilyName}>
                {familyName.charAt(0).toUpperCase() + familyName.slice(1)}
              </div>
              <div className={styles.sequentialColors}>
                {Object.entries(colors).map(([shadeName, color]) => {
                  const isLight = shadeName.includes('tint');
                  return (
                    <div
                      key={shadeName}
                      className={styles.sequentialColor}
                      style={{
                        backgroundColor: color,
                        color: isLight ? '#000' : '#fff',
                      }}
                    >
                      <span className={styles.sequentialColorName}>{shadeName}</span>
                      <span className={styles.sequentialColorValue}>{color}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Diverging Palettes */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>4. Diverging Palettes (Red-to-Green)</h2>
        <p className={styles.sectionDescription}>
          For data that diverges from a central midpoint (e.g., positive/negative, above/below average)
        </p>
        
        <h3 className={styles.subsectionTitle}>
          5-Color Diverging
        </h3>
        <div className={styles.divergingBar}>
          {dataVisualizationTokens.diverging.redToGreen5.map((color, index) => (
            <div
              key={index}
              className={styles.divergingSegment}
              style={{ backgroundColor: color }}
            >
              <span className={styles.divergingLabel}>
                {index === 0 ? 'Negative' : index === 2 ? 'Neutral' : index === 4 ? 'Positive' : ''}
              </span>
            </div>
          ))}
        </div>

        <h3 className={styles.subsectionTitle}>
          7-Color Diverging
        </h3>
        <div className={styles.divergingBar}>
          {dataVisualizationTokens.diverging.redToGreen7.map((color, index) => (
            <div
              key={index}
              className={styles.divergingSegment}
              style={{ backgroundColor: color }}
            >
              <span className={styles.divergingLabel}>
                {index === 3 ? 'Neutral' : ''}
              </span>
            </div>
          ))}
        </div>

        <h3 className={styles.subsectionTitle}>
          9-Color Diverging
        </h3>
        <div className={styles.divergingBar}>
          {dataVisualizationTokens.diverging.redToGreen9.map((color, index) => (
            <div
              key={index}
              className={styles.divergingSegment}
              style={{ backgroundColor: color }}
            >
              <span className={styles.divergingLabel}>
                {index === 4 ? 'Neutral' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Threat Colors */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>5. Threat Colors (Status Indicators)</h2>
        <p className={styles.sectionDescription}>
          To indicate threat levels, security status, or performance health
        </p>
        
        <h3 className={styles.subsectionTitle}>
          3-Level Threat
        </h3>
        <div className={styles.colorGrid}>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.critical }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>critical</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.critical}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.warning }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>warning</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.warning}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.safe }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>safe</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.safe}</div>
            </div>
          </div>
        </div>

        <h3 className={styles.subsectionTitle}>
          5-Level Threat
        </h3>
        <div className={styles.colorGrid}>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.severe }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>severe</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.severe}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.moderate }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>moderate</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.moderate}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.elevated }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>elevated</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.elevated}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.low }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>low</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.low}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.threat.minimal }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>minimal</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.threat.minimal}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Semantic Colors */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>6. Semantic Colors</h2>
        <p className={styles.sectionDescription}>
          For data with inherent meaning (positive/negative, success/error)
        </p>
        <div className={styles.colorGrid}>
          {Object.entries(dataVisualizationTokens.semantic).map(([key, color]) => (
            <div key={key} className={styles.colorCard}>
              <div className={styles.colorSwatch} style={{ backgroundColor: color }} />
              <div className={styles.colorInfo}>
                <div className={styles.colorName}>{key}</div>
                <div className={styles.colorValue}>{color}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chart-Specific Colors */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>7. Chart-Specific Colors</h2>
        <p className={styles.sectionDescription}>
          Specialized colors for specific chart types
        </p>
        
        <div className={styles.colorGrid}>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.chart.primaryBar }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>primaryBar</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.chart.primaryBar}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.chart.secondaryBar }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>secondaryBar</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.chart.secondaryBar}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.chart.primaryBubble }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>primaryBubble</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.chart.primaryBubble}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.chart.secondaryBubble }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>secondaryBubble</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.chart.secondaryBubble}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.chart.heatmapLow }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>heatmapLow</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.chart.heatmapLow}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.chart.heatmapMid }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>heatmapMid</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.chart.heatmapMid}</div>
            </div>
          </div>
          <div className={styles.colorCard}>
            <div className={styles.colorSwatch} style={{ backgroundColor: dataVisualizationTokens.chart.heatmapHigh }} />
            <div className={styles.colorInfo}>
              <div className={styles.colorName}>heatmapHigh</div>
              <div className={styles.colorValue}>{dataVisualizationTokens.chart.heatmapHigh}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Practical Examples */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>8. Practical Chart Examples</h2>
        <p className={styles.sectionDescription}>
          Real-world usage examples with interactive elements
        </p>
        
        <div className={styles.chartExample}>
          {/* Bar Chart Example */}
          <div className={styles.barChart}>
            <h3 className={styles.barChartTitle}>Product Sales (Bar Chart)</h3>
            <div className={styles.bars}>
              {barChartData.map((item, index) => (
                <div key={index} className={styles.bar}>
                  <div className={styles.barLabel}>{item.label}</div>
                  <div
                    className={styles.barFill}
                    style={{
                      backgroundColor: item.color,
                      width: `${item.value}%`,
                    }}
                  >
                    {item.value}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pie Chart Example */}
          <div className={styles.pieChart}>
            <h3 className={styles.pieChartTitle}>Customer Satisfaction (Pie Chart)</h3>
            <div
              className={styles.pieCircle}
              style={{
                background: `conic-gradient(${getPieGradient()})`,
              }}
            />
            <div className={styles.pieLegend}>
              {pieChartData.map((item, index) => (
                <div key={index} className={styles.legendItem}>
                  <div
                    className={styles.legendColor}
                    style={{ backgroundColor: item.color }}
                  />
                  <span>{item.label}: {item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Microsoft Teams Colors */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>7. Microsoft Teams Colors</h2>
        <p className={styles.sectionDescription}>
          Specialized color palette for Microsoft Teams product integrations
        </p>
        <div className={styles.paletteGrid}>
          {Object.entries(dataVisualizationTokens.teams).map(([key, value]) => (
            <div key={key} className={styles.colorCard}>
              <div
                className={styles.colorSwatch}
                style={{ backgroundColor: value }}
              />
              <div className={styles.colorInfo}>
                <div className={styles.colorName}>{key}</div>
                <div className={styles.colorValue}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Threat Diverging Colors */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>8. Threat Diverging Colors</h2>
        <p className={styles.sectionDescription}>
          Color scales for threat indicators and security visualizations with light/dark mode support
        </p>
        
        <h3 className={styles.subsectionTitle}>Light Mode</h3>
        <div className={styles.paletteGrid}>
          {Object.entries(dataVisualizationTokens.threatDiverging.light).map(([key, value]) => (
            <div key={key} className={styles.colorCard}>
              <div
                className={styles.colorSwatch}
                style={{ backgroundColor: value }}
              />
              <div className={styles.colorInfo}>
                <div className={styles.colorName}>{key}</div>
                <div className={styles.colorValue}>{value}</div>
              </div>
            </div>
          ))}
        </div>

        <h3 className={styles.subsectionTitle}>Dark Mode</h3>
        <div className={styles.paletteGrid}>
          {Object.entries(dataVisualizationTokens.threatDiverging.dark).map(([key, value]) => (
            <div key={key} className={styles.colorCard}>
              <div
                className={styles.colorSwatch}
                style={{ backgroundColor: value }}
              />
              <div className={styles.colorInfo}>
                <div className={styles.colorName}>{key}</div>
                <div className={styles.colorValue}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
